import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, LayoutGrid, Building2, FileText, Settings,
  Mail, CalendarCheck, LogOut, Menu, X, Star, TrendingUp, DollarSign, Video, Globe, Repeat, Inbox,
} from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { EXPERTS, INVESTORS, SITE_SETTINGS } from "../../mock/mock";
import { isAuthed, setAuthed } from "../../lib/adminAuth";
import { LanguageSwitcher } from "../../components/public/Header";
import { AdminExperts, AdminSimpleList, AdminSettings, AdminContent, AdminCategories, AdminInvestors } from "./adminViews";
import Overview from "./adminOverview";
import { Toaster } from "../../components/ui/toaster";

const NAV = [
  { id: "overview", icon: LayoutDashboard, key: "dashboard" },
  { id: "experts", icon: Users, key: "experts" },
  { id: "categories", icon: LayoutGrid, key: "categories" },
  { id: "investors", icon: Building2, key: "investors" },
  { id: "content", icon: FileText, key: "content" },
  { id: "bookings", icon: CalendarCheck, key: "bookings" },
  { id: "subscriptions", icon: Repeat, key: "subscriptions" },
  { id: "requests", icon: Inbox, key: "requests" },
  { id: "applications", icon: Users, key: "applications" },
  { id: "subscribers", icon: Mail, key: "subscribers" },
  { id: "settings", icon: Settings, key: "settings" },
];

const AdminDashboard = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [active, setActive] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!isAuthed()) navigate("/admin/login");
  }, [navigate]);

  const logout = () => {
    setAuthed(false);
    navigate("/admin/login");
  };

  const render = () => {
    switch (active) {
      case "overview": return <Overview />;
      case "experts": return <AdminExperts />;
      case "categories": return <AdminCategories />;
      case "investors": return <AdminInvestors />;
      case "bookings": return <AdminSimpleList type="bookings" />;
      case "subscriptions": return <AdminSimpleList type="subscriptions" />;
      case "requests": return <AdminSimpleList type="requests" />;
      case "applications": return <AdminSimpleList type="applications" />;
      case "subscribers": return <AdminSimpleList type="subscribers" />;
      case "content": return <AdminContent />;
      case "settings": return <AdminSettings />;
      default: return <Overview />;
    }
  };

  return (
    <div className="min-h-screen bg-neutral-100 flex">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 h-screen z-40 w-64 bg-neutral-950 text-white flex flex-col transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full rtl:translate-x-full"} lg:translate-x-0`}>
        <div className="p-5 flex items-center gap-2 border-b border-neutral-800">
          <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center">
            <span className="text-neutral-900 font-display font-extrabold text-lg">i</span>
          </div>
          <span className="font-display font-extrabold text-lg">intro <span className="text-neutral-500 font-medium text-sm">admin</span></span>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto no-scrollbar">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <button
                key={n.id}
                onClick={() => { setActive(n.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-colors ${
                  active === n.id ? "bg-indigo-600 text-white" : "text-neutral-400 hover:bg-neutral-900 hover:text-white"
                }`}
              >
                <Icon className="w-5 h-5" /> {t(`admin.${n.key}`)}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-neutral-800">
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-neutral-400 hover:bg-neutral-900 hover:text-white transition-colors">
            <LogOut className="w-5 h-5" /> {t("admin.logout")}
          </button>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur border-b border-neutral-200 px-5 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden w-9 h-9 flex items-center justify-center rounded-xl hover:bg-neutral-100">
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="font-display font-extrabold text-xl sm:text-2xl text-neutral-900">{t(`admin.${NAV.find(n=>n.id===active)?.key}`)}</h1>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher compact />
            <button onClick={() => window.open("/", "_blank")} className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900 px-3 py-2 rounded-full hover:bg-neutral-100">
              <Globe className="w-4 h-4" /> Siteyi Gör
            </button>
            <div className="w-9 h-9 rounded-full bg-neutral-900 text-white flex items-center justify-center text-sm font-bold">A</div>
          </div>
        </header>
        <main className="p-5 sm:p-8 max-w-6xl mx-auto">{render()}</main>
      </div>
      <Toaster />
    </div>
  );
};

export default AdminDashboard;
